import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Icon, Modal } from 'antd'
import { processLogout } from 'utils'

const STORAGE_KEY = 'gads-activity-timestamp'

const InactivityModal = ({ userIdleTime, userIdleWarningTime }) => {
  const [showIdleModal, setShowIdleModal] = useState(false)
  const [countdown, setCountdown] = useState(userIdleWarningTime)
  const [lastActivityTime, setLastActivityTime] = useState(Date.now())

  const lastActivityRef = useRef(lastActivityTime)
  const lastWriteRef = useRef(0)

  useEffect(() => {
    lastActivityRef.current = lastActivityTime
  }, [lastActivityTime])

  const handleStayLoggedIn = useCallback(() => {
    const now = Date.now()
    localStorage.setItem(STORAGE_KEY, 'stay-' + now)
    setLastActivityTime(now)
    lastActivityRef.current = now // Update ref directly to ensure immediate effect
    setShowIdleModal(false)
    setCountdown(userIdleWarningTime)
  }, [userIdleWarningTime])

  const handleFinalizeLogout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    processLogout()
  }, [])

  const handleLogout = useCallback(() => {
    const now = Date.now()
    localStorage.setItem(STORAGE_KEY, 'logout-' + now)
    setShowIdleModal(false)
    handleFinalizeLogout()
  }, [handleFinalizeLogout])

  const idleTimeout = useMemo(() => {
    return userIdleTime > 0 ? userIdleTime * 60 * 1000 : null
  }, [userIdleTime])

  useEffect(() => {
    const intervalId = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current
      if (idleTimeout && elapsed > idleTimeout) {
        const warningElapsed = elapsed - idleTimeout
        const secondsLeft = Math.max(
          userIdleWarningTime - Math.ceil(warningElapsed / 1000),
          0
        )
        setCountdown(secondsLeft)

        if (showIdleModal && secondsLeft <= 0) {
          handleLogout()
        } else if (!showIdleModal) {
          setShowIdleModal(true)
        }
      } else if (showIdleModal) {
        // Close the modal if the user is no longer idle
        setShowIdleModal(false)
        setCountdown(userIdleWarningTime)
      }
    }, 1000)

    return () => clearInterval(intervalId)
  }, [handleLogout, idleTimeout, showIdleModal, userIdleWarningTime])

  useEffect(() => {
    const handler = event => {
      if (event.key === STORAGE_KEY && event.newValue) {
        const value = event.newValue
        if (value.startsWith('stay-')) {
          const timestamp = parseInt(value.split('-')[1])
          if (!isNaN(timestamp)) {
            setCountdown(userIdleWarningTime)
            setShowIdleModal(false)
            setLastActivityTime(timestamp)
          }
        } else if (value.startsWith('logout-')) {
          setShowIdleModal(false)
          handleFinalizeLogout()
        } else {
          const timestamp = parseInt(value)
          if (!isNaN(timestamp)) {
            setLastActivityTime(timestamp)
          }
        }
      }
    }

    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [handleFinalizeLogout, userIdleWarningTime])

  useEffect(() => {
    if (showIdleModal) return

    const events = ['mousemove', 'keydown', 'scroll', 'touchstart']
    const reset = () => {
      const now = Date.now()
      if (now - lastWriteRef.current > 1000) {
        lastWriteRef.current = now
        localStorage.setItem(STORAGE_KEY, now.toString())
        setLastActivityTime(now)
      }
    }

    events.forEach(event => window.addEventListener(event, reset, true))
    return () => {
      events.forEach(event => window.removeEventListener(event, reset, true))
    }
  }, [showIdleModal])

  return (
    <Modal
      visible={showIdleModal}
      title={
        <span>
          <Icon
            type="warning"
            style={{ color: '#faad14', marginRight: 8, fontSize: 20 }}
          />
          Session Timeout Warning
        </span>
      }
      maskClosable={false}
      closable={false}
      footer={[
        <Button key="stay" type="primary" onClick={handleStayLoggedIn}>
          Stay Logged In
        </Button>,
        <Button key="logout" onClick={handleLogout}>
          Log Out
        </Button>,
      ]}
    >
      <p>
        You will be logged out in <b>{countdown}</b> seconds due to inactivity.
      </p>
      <p>Do you want to stay logged in?</p>
    </Modal>
  )
}

export default InactivityModal
